// client/src/features/payments/enums.ts — the two payment wire enums, as runtime lists · PAY-003.
//
// `features/rent/enums.ts` is the shape this copies, and its reasoning holds unchanged: the
// generated client gives `PaymentMethod` and `PaymentSource` as string-literal UNIONS, and a
// union has no runtime value — the method `<select>` cannot iterate one and `z.enum()` cannot
// validate a search param against one. So the members are spelled out here and HELD to the
// union by an exhaustive `Record<…, true>`, which fails `tsc` in the PR that regenerates the
// client if a member is added or dropped.
//
// Both lists are also the `enums.{EnumName}.{VALUE}` key segments of 04 §9.6.3's one sanctioned
// computed key. That is exactly why they must be held to the contract rather than typed freely:
// a name that drifts renders its own key path to a landlord. `__tests__/enum-parity.test.ts`
// closes the other half — the i18n gate cannot see a computed key, so it cannot notice a label
// that was never written (the blind spot D-395 found on the board's five statuses).

import type { PaymentMethod, PaymentSource } from '@/client';

/** 03 §8.7's `ck_payments_method`, in the enum's own declaration order (SharedKernel `Enums.cs`). */
const PAYMENT_METHOD_ORDER: Record<PaymentMethod, true> = {
  BANK_TRANSFER: true,
  CASH: true,
  CARD: true,
  DIRECT_DEBIT: true,
  COMPENSATION: true,
  OTHER: true,
};

export const PAYMENT_METHODS = Object.keys(PAYMENT_METHOD_ORDER) as readonly PaymentMethod[];

/**
 * `MANUAL` · `BANK_IMPORT`. In V1 only the first is reachable — `Payment.Register` writes
 * `PaymentSource.MANUAL` and nothing else writes a payment at all, because D-233 deferred
 * reconciliation whole and BANK-003 with it. The second member is rendered anyway rather than
 * hidden: it is on the contract, a restored or imported row could carry it, and a chip that
 * silently renders nothing is worse than one that says where the money came from. See D-706.
 */
const PAYMENT_SOURCE_ORDER: Record<PaymentSource, true> = {
  MANUAL: true,
  BANK_IMPORT: true,
};

export const PAYMENT_SOURCES = Object.keys(PAYMENT_SOURCE_ORDER) as readonly PaymentSource[];
